import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Pill } from "@/pages/admin/AdminLayout";
import { Card, btn, btnGhost, inputClass } from "@/pages/admin/email/EmailLayout";

const STATUSES = ["active", "pending", "unsubscribed", "bounced", "complained"];

export default function EmailSubscribers() {
  const [filters, setFilters] = useState({ q: "", tag: "", status: "", source: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data: res } = await api.get("/admin/email/subscribers", { params });
    setData(res);
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api.get("/admin/email/tags").then((r) => setTags(r.data.map((t) => t.name)));
  }, []);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const allSelected = data.items.length > 0 && selected.length === data.items.length;

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/email/subscribers/${id}`, body);
      toast.success("Subscriber updated");
      load();
      if (detail?.subscriber?.id === id) openDetail(id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const bulkTag = async (tag) => {
    if (!tag || selected.length === 0) return;
    for (const id of selected) {
      const sub = data.items.find((s) => s.id === id);
      const next = Array.from(new Set([...(sub?.tags || []), tag]));
      await api.patch(`/admin/email/subscribers/${id}`, { tags: next });
    }
    toast.success(`Tagged ${selected.length} subscriber(s) as ${tag}`);
    setSelected([]);
    load();
  };

  const openDetail = async (id) => {
    const { data: res } = await api.get(`/admin/email/subscribers/${id}`);
    setDetail(res);
    window.requestAnimationFrame(() =>
      document
        .querySelector('[data-testid="subscriber-detail"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const exportCsv = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const res = await api.get("/admin/email/subscribers.csv", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hhl-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="email-subscribers">
      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Search email or name"
          data-testid="subscriber-search"
          className={`${inputClass} max-w-[260px]`}
        />
        <select
          value={filters.tag}
          onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value }))}
          data-testid="subscriber-filter-tag"
          className={`${inputClass} max-w-[200px]`}
        >
          <option value="">All tags</option>
          {tags.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          data-testid="subscriber-filter-status"
          className={`${inputClass} max-w-[180px]`}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <input
          value={filters.source}
          onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          placeholder="Signup source"
          data-testid="subscriber-filter-source"
          className={`${inputClass} max-w-[200px]`}
        />
        <button onClick={exportCsv} data-testid="subscriber-export" className={btnGhost}>
          Export CSV
        </button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-sage-soft border border-sage/30 px-4 py-3">
          <span className="text-[13px] text-charcoal" data-testid="subscriber-selected-count">
            {selected.length} selected
          </span>
          <select
            onChange={(e) => {
              bulkTag(e.target.value);
              e.target.value = "";
            }}
            data-testid="subscriber-bulk-tag"
            className={`${inputClass} max-w-[220px]`}
          >
            <option value="">Add a tag…</option>
            {tags.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <button onClick={() => setSelected([])} className={btnGhost}>
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white border border-line overflow-x-auto" data-testid="subscribers-table">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-bone border-b border-line">
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => setSelected(e.target.checked ? data.items.map((s) => s.id) : [])}
                  data-testid="subscriber-select-all"
                  className="h-4 w-4 accent-[#7a8b73]"
                />
              </th>
              {["Email", "Status", "Tags", "Source", "Added", ""].map((h) => (
                <th
                  key={h || "a"}
                  className="px-3 py-3 text-[9.5px] tracking-[0.16em] uppercase text-[#6E6A63] font-medium"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-[13px] font-light text-[#6E6A63]">
                  No subscribers match these filters.
                </td>
              </tr>
            )}
            {data.items.map((s) => (
              <tr key={s.id} className="border-b border-line/70" data-testid={`subscriber-row-${s.email}`}>
                <td className="px-3 py-3.5">
                  <input
                    type="checkbox"
                    checked={selected.includes(s.id)}
                    onChange={() => toggle(s.id)}
                    data-testid={`subscriber-select-${s.email}`}
                    className="h-4 w-4 accent-[#7a8b73]"
                  />
                </td>
                <td className="px-3 py-3.5 text-[13.5px] text-charcoal break-all max-w-[220px]">
                  {s.email}
                  {s.first_name ? (
                    <span className="block text-[11.5px] text-[#8A867F]">{s.first_name}</span>
                  ) : null}
                </td>
                <td className="px-3 py-3.5">
                  <Pill tone={s.status === "active" ? "green" : s.status === "pending" ? "yellow" : "red"}>
                    {s.status}
                  </Pill>
                </td>
                <td className="px-3 py-3.5 text-[11.5px] font-light text-[#5A5A5A] max-w-[180px]">
                  {(s.tags || []).join(", ") || "—"}
                </td>
                <td className="px-3 py-3.5 text-[12px] font-light text-[#5A5A5A]">
                  {s.last_source || s.source || "—"}
                </td>
                <td className="px-3 py-3.5 text-[12px] font-light text-[#6E6A63] whitespace-nowrap">
                  {String(s.created_at || "").slice(0, 10)}
                </td>
                <td className="px-3 py-3.5 text-right whitespace-nowrap">
                  <button
                    onClick={() => openDetail(s.id)}
                    data-testid={`subscriber-view-${s.email}`}
                    className="text-[11px] tracking-[0.12em] uppercase text-sage hover:text-sage-dark mr-4"
                  >
                    View
                  </button>
                  {s.status !== "unsubscribed" && (
                    <button
                      onClick={() => patch(s.id, { status: "unsubscribed" })}
                      data-testid={`subscriber-unsubscribe-${s.email}`}
                      className="text-[11px] tracking-[0.12em] uppercase text-[#991B1B]/70 hover:text-[#991B1B]"
                    >
                      Unsubscribe
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[12.5px] font-light text-[#8A867F]">
        {data.total} subscriber record(s) match. Unsubscribed, bounced and complained contacts are
        always excluded from campaigns.
      </p>

      {detail && (
        <Card title={`Subscriber — ${detail.subscriber.email}`} testid="subscriber-detail">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="text-[13px] font-light text-[#5A5A5A] space-y-1.5">
              <div>Status: {detail.subscriber.status}</div>
              <div>Signed up: {detail.subscriber.created_at}</div>
              <div>Confirmed: {detail.subscriber.confirmed_at || "not yet"}</div>
              <div>Source: {detail.subscriber.last_source || detail.subscriber.source}</div>
              <div>Consent: {String(detail.subscriber.consent?.granted)} ({detail.subscriber.consent?.at})</div>
              <div>Consent version: {detail.subscriber.consent?.version}</div>
              <div>Welcome email: {detail.subscriber.welcome_sent_at || "not sent"}</div>
              <div>Unsubscribed: {detail.subscriber.unsubscribed_at || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#6E6A63] mb-2">Tags</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map((t) => {
                  const on = (detail.subscriber.tags || []).includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() =>
                        patch(detail.subscriber.id, {
                          tags: on
                            ? detail.subscriber.tags.filter((x) => x !== t)
                            : [...(detail.subscriber.tags || []), t],
                        })
                      }
                      data-testid={`subscriber-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-3 py-1.5 text-[12px] border transition-colors ${
                        on ? "bg-sage border-sage text-white" : "border-line text-[#5A5A5A]"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#6E6A63] mb-2">
                Email history
              </div>
              {detail.history.length === 0 && (
                <p className="text-[12.5px] font-light text-[#8A867F]">No emails yet.</p>
              )}
              {detail.history.map((h) => (
                <div key={h.id} className="text-[12.5px] font-light text-[#5A5A5A]">
                  {String(h.created_at).slice(0, 16)} — {h.kind} — {h.status}
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => setDetail(null)} className={`${btn} mt-6`}>
            Close
          </button>
        </Card>
      )}
    </div>
  );
}
